import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Link,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import KeyIcon from "@mui/icons-material/Key";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EventIcon from "@mui/icons-material/Event";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import StorageIcon from "@mui/icons-material/Storage";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import VerifiedIcon from "@mui/icons-material/Verified";
import HistoryIcon from "@mui/icons-material/History";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import ApiIcon from "@mui/icons-material/Api";
import BuildIcon from "@mui/icons-material/Build";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import SettingsBrightnessOutlinedIcon from "@mui/icons-material/SettingsBrightnessOutlined";
import CheckIcon from "@mui/icons-material/Check";
import GavelIcon from "@mui/icons-material/Gavel";
import { ENTITY_CONFIGS } from "../pages/entities/configs";
import ReportIssueDialog from "./ReportIssueDialog";
import { useThemeMode } from "../context/ThemeModeContext";
import {
  useUserPermissions,
  clearPermissionsCache
} from "../hooks/useUserPermissions";
import type { ThemeMode } from "../theme";

const DRAWER_WIDTH = 260;

const TOOLS_NAV = [
  { label: "Compliance Lookup", path: "/lookup", icon: <FactCheckIcon /> },
  { label: "Compliance Diff", path: "/diff", icon: <CompareArrowsIcon /> },
  { label: "Deadlines Engine", path: "/deadlines", icon: <EventIcon /> }
];

const ASSESSMENTS_NAV = [
  { label: "Assessment Log", path: "/assessments", icon: <AssessmentIcon /> },
  {
    label: "Saved Assessments",
    path: "/saved-assessments",
    icon: <BookmarkIcon />
  }
];

const API_NAV = [
  { label: "API Dashboard", path: "/api/dashboard", icon: <DashboardIcon /> },
  { label: "API Keys", path: "/keys", icon: <KeyIcon /> },
  { label: "API Docs", path: "/docs", icon: <MenuBookIcon /> }
];

const OPS_NAV = [
  { label: "Validation", path: "/validation", icon: <VerifiedIcon /> },
  { label: "Audit Log", path: "/audit", icon: <HistoryIcon /> },
  { label: "Changelog", path: "/changelog", icon: <HistoryEduIcon /> }
];

const ENTITY_NAV = Object.entries(ENTITY_CONFIGS).map(([slug, cfg]) => ({
  slug,
  label: cfg.title,
  path: `/entities/${slug}`
}));

const themeModeOptions: {
  value: ThemeMode;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "light",
    label: "Light",
    icon: <LightModeOutlinedIcon fontSize="small" />
  },
  {
    value: "dark",
    label: "Dark",
    icon: <DarkModeOutlinedIcon fontSize="small" />
  },
  {
    value: "system",
    label: "System",
    icon: <SettingsBrightnessOutlinedIcon fontSize="small" />
  }
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [entitiesOpen, setEntitiesOpen] = useState(true);
  const [opsOpen, setOpsOpen] = useState(true);
  const [apiOpen, setApiOpen] = useState(true);
  const [assessmentsOpen, setAssessmentsOpen] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth0();
  const { mode: themeMode, setMode: setThemeMode } = useThemeMode();
  const { isEditor, isDeveloper, isAdmin, roleLabel, loading: permissionsLoading } =
    useUserPermissions();

  const handleAvatarClick = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    clearPermissionsCache();
    handleMenuClose();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const navTo = (path: string) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false);
  };

  const navItemSx = {
    mx: 1,
    borderRadius: 2,
    mb: 0.5,
    "&.Mui-selected": {
      backgroundColor: "rgba(74,114,232,0.10)",
      "&:hover": { backgroundColor: "rgba(74,114,232,0.16)" }
    }
  };

  const isActive = (path: string) =>
    pathname === path || (path !== "/" && pathname.startsWith(path + "/"));

  const renderNavItem = (
    label: string,
    path: string,
    icon: React.ReactNode,
    indent = false
  ) => {
    const active = isActive(path);
    return (
      <ListItem key={path} disablePadding>
        <ListItemButton
          selected={active}
          onClick={() => navTo(path)}
          sx={{ ...navItemSx, ...(indent ? { pl: 4 } : {}) }}
        >
          <ListItemIcon
            sx={{
              minWidth: indent ? 28 : 36,
              color: active ? "primary.main" : "text.secondary"
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              variant: indent ? "body2" : "body1",
              fontWeight: active ? 600 : 400,
              color: active ? "primary.main" : "text.primary"
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderSection = (
    label: string,
    icon: React.ReactNode,
    open: boolean,
    toggle: () => void,
    items: {
      label: string;
      path: string;
      icon: React.ReactNode;
      slug?: string;
    }[]
  ) => (
    <>
      <ListItem disablePadding>
        <ListItemButton onClick={toggle} sx={{ ...navItemSx }}>
          <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>
            {icon}
          </ListItemIcon>
          <ListItemText primary={label} />
          {open ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding dense>
          {items.map(({ label: l, path, icon: ic }) =>
            renderNavItem(l, path, ic, true)
          )}
        </List>
      </Collapse>
    </>
  );

  const drawer = (
    <Box display="flex" flexDirection="column" height="100%">
      <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <GavelIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} color="primary">
            CREED
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, overflowY: "auto", pt: 1 }}>
        {/* Dashboard — all roles */}
        {renderNavItem("Dashboard", "/", <DashboardIcon />)}

        {/* Tools — all roles */}
        {renderSection(
          "Tools",
          <BuildIcon />,
          toolsOpen,
          () => setToolsOpen((v) => !v),
          TOOLS_NAV
        )}

        {/* Assessments + Reference Data — Editor+ */}
        {!permissionsLoading && isEditor && (
          <>
            {renderSection(
              "Assessments",
              <AssessmentIcon />,
              assessmentsOpen,
              () => setAssessmentsOpen((v) => !v),
              ASSESSMENTS_NAV
            )}
            {renderSection(
              "Reference Data",
              <StorageIcon />,
              entitiesOpen,
              () => setEntitiesOpen((v) => !v),
              ENTITY_NAV.map(({ slug, label, path }) => ({
                label,
                path,
                icon: <CircleIcon sx={{ fontSize: 6 }} />,
                slug
              }))
            )}
          </>
        )}

        {/* API — Developer+ */}
        {!permissionsLoading &&
          isDeveloper &&
          renderSection(
            "API",
            <ApiIcon />,
            apiOpen,
            () => setApiOpen((v) => !v),
            API_NAV
          )}

        {/* Operations — Admin only */}
        {!permissionsLoading &&
          isAdmin &&
          renderSection(
            "Operations",
            <VerifiedIcon />,
            opsOpen,
            () => setOpsOpen((v) => !v),
            OPS_NAV
          )}
      </List>
      <Divider />
      <Box px={2} py={1.5}>
        <Typography variant="caption" color="text.secondary">
          CREED Admin
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setDrawerOpen((v) => !v)}
            sx={{ mr: 1 }}
            aria-label="Toggle navigation"
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              minWidth: 0
            }}
          >
            <Link
              href={import.meta.env.VITE_ATLAS_URL}
              underline="hover"
              variant="h6"
              fontWeight={700}
              noWrap
              sx={{ flexShrink: 0 }}
            >
              Atlas
            </Link>
            <Typography
              variant="h6"
              fontWeight={700}
              noWrap
              color="text.secondary"
              sx={{ cursor: "pointer", minWidth: 0 }}
              onClick={() => navigate("/")}
            >
              {" / Compliance Rules Engine Database"}
            </Typography>
            <Chip
              label={permissionsLoading ? "…" : roleLabel}
              size="small"
              color={
                permissionsLoading
                  ? "default"
                  : isAdmin
                    ? "secondary"
                    : isDeveloper
                      ? "info"
                      : isEditor
                        ? "primary"
                        : "default"
              }
              sx={{ ml: 1, fontWeight: 600, fontSize: 12, flexShrink: 0 }}
            />
          </Box>
          <Tooltip title={user?.name ?? user?.email ?? "Account"}>
            <IconButton onClick={handleAvatarClick} size="small">
              <Avatar
                src={user?.picture}
                alt={user?.name}
                sx={{ width: 32, height: 32, fontSize: 14 }}
              >
                {(user?.name ?? user?.email ?? "U")[0].toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { minWidth: 220 } } }}
          >
            {/* User info */}
            <Box px={2} py={1}>
              <Typography variant="body2" fontWeight={600}>
                {user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />

            <MenuItem
              onClick={() => {
                handleMenuClose();
                setReportIssueOpen(true);
              }}
            >
              <ListItemIcon>
                <FeedbackOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
                Report Issue
              </ListItemText>
            </MenuItem>
            <Divider sx={{ mt: 0.5 }} />

            {/* Appearance */}
            <Box px={2} pt={1} pb={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                letterSpacing={0.5}
                textTransform="uppercase"
              >
                Appearance
              </Typography>
            </Box>
            {themeModeOptions.map(({ value, label, icon }) => (
              <MenuItem
                key={value}
                selected={themeMode === value}
                onClick={() => setThemeMode(value)}
                sx={{ gap: 1 }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color:
                      themeMode === value ? "primary.main" : "text.secondary"
                  }}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    color: themeMode === value ? "primary.main" : "text.primary"
                  }}
                >
                  {label}
                </ListItemText>
                {themeMode === value && (
                  <CheckIcon
                    sx={{ fontSize: 16, color: "primary.main", ml: 1 }}
                  />
                )}
              </MenuItem>
            ))}
            <Divider sx={{ mt: 0.5 }} />

            {/* Sign out */}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
                Sign out
              </ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          }),
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" }
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          // 64px is the height of the app bar
          // pt: "64px",
          pt: 11,
          px: 3,
          minHeight: "100vh"
        }}
      >
        {children}
      </Box>
      <ReportIssueDialog
        open={reportIssueOpen}
        onClose={() => setReportIssueOpen(false)}
      />
    </Box>
  );
}
